import React, { useCallback, useContext, useMemo, useState, type JSX } from 'react';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { EDTextEdit } from '../../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { useAttributeValues } from '../../../../../_Common/Hooks/useAttributeValues';
import { reduceFakturasidor } from '../../../LeverantorFaktura/Queries/Fakturabilder/FakturabilderQuery';
import { IFakturaBildPDF } from '../../../../../../../Components/ExportToPDF/ExportToPDFArgumentTypes';
import { useIsBreakpointOrSmaller } from '../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import {
  IMailArgs,
  sendReportAsMail,
} from '../../../../../../../Components/ExportToPDF/ExportToPDFUtils';
import { getServerURL } from '../../../../../../../Utils/getServerURL';
import { convertHtmlFakturaToBase64PNG } from '../../../../../Ekonomi/Rutiner/EkonomiskAnalys/Components/VerifikationModal/verifikationUtils';
import { isEmailValid } from '../../../../../../../Utils/emailUtils';

interface IProps {
  onClose: () => void;
  open: boolean;
}

interface IFakturaBildMail extends IMailArgs, IFakturaBildPDF {}

const useStyles = makeStyles()((theme: Theme) => ({
  dialogContentWrapper: {
    margin: 0,
  },
  dialogHeader: {
    zIndex: 2,
  },
  dialogPaper: {
    [theme.breakpoints.up('md')]: {
      maxHeight: 'calc(100% - 16px)',
    },
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
}));

enum BannerTypeEnum {
  INFO = 'Info',
  ERROR = 'Error',
}

const FakturaattestMailFakturaBildModal: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { onClose } = props;
  const dataformContext = useContext(BusinessDataFormContext);
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const { classes } = useStyles();
  const { value: verNr } = useAttributeValues('Faktura.DefAnkVernr');
  const isSmallScreen = useIsBreakpointOrSmaller('md');

  const [receivers, setReceivers] = useState('');
  const [copyReceivers, setCopyReceivers] = useState('');
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState<{
    type: BannerTypeEnum;
    message: string;
  } | null>();

  const images = useMemo(
    () => reduceFakturasidor(dataformContext?.data?._Associations?.Faktura),
    [dataformContext?.data?._Associations?.Faktura]
  );
  const date = useMemo(() => new Date().toLocaleDateString('sv-SE'), []);

  const args: IFakturaBildMail = useMemo(
    () => ({
      reportName: 'Fakturabild',
      dbname: activeDatabase ?? '',
      verifikationsNr: verNr ?? '',
      fileName: 'fakturabild_' + verNr + '_' + date,
      fakturaBildIds: images?.bilder ?? [],
      xmlFaktura: Boolean(images?.xml),
      bokfDatum: '',
      journalNr: '',
      skannadSvefaktura: Boolean(images?.skannadSvefaktura),
      receivers,
      copyReceivers,
      body: mailBody,
      subject: mailSubject,
      sendMail: true,
    }),
    [
      activeDatabase,
      copyReceivers,
      date,
      images?.bilder,
      images?.skannadSvefaktura,
      images?.xml,
      mailBody,
      mailSubject,
      receivers,
      verNr,
    ]
  );

  const isSuccessful = useMemo(
    () => statusText?.type === BannerTypeEnum.INFO,
    [statusText?.type]
  );

  const validateInputs = useCallback((): JSX.Element[] => {
    const disabledReasons: JSX.Element[] = [];

    if (!isEmailValid(args.receivers) && args.receivers !== '') {
      disabledReasons.push(
        <div>Mailadressen är inte i ett giltigt format</div>
      );
    } else if (args.receivers === '' || args.receivers == undefined) {
      disabledReasons.push(<div>Du måste ange en mottagare</div>);
    }
    if (!isEmailValid(args?.copyReceivers ?? '') && args.copyReceivers !== '') {
      disabledReasons.push(
        <div>Mailadressen för kopia är inte i ett giltigt format</div>
      );
    }
    if (args.subject === '' || args.subject == undefined) {
      disabledReasons.push(<div>Du måste ange ett ämne</div>);
    }

    return disabledReasons;
  }, [args.copyReceivers, args.receivers, args.subject]);

  const onSendClick = useCallback(() => {
    setLoading(true);
    setStatusText(null);
    if (args.xmlFaktura && !args.skannadSvefaktura) {
      fetch(`${getServerURL()}/blob?id=${images?.bilder?.[0]}`, {
        method: 'POST',
        credentials: 'include',
      })
        .then(res => res.text())
        .then(html => {
          convertHtmlFakturaToBase64PNG(html).then(imageData => {
            sendReportAsMail<IFakturaBildMail>({
              ...args,
              base64ImgData: imageData,
              skannadSvefaktura: false,
            }).then(
              value => {
                setStatusText({ message: value, type: BannerTypeEnum.INFO });
                setLoading(false);
                setTimeout(() => {
                  onClose();
                }, 2500);
              },
              (value: string) => {
                setStatusText({ message: value, type: BannerTypeEnum.ERROR });
                setLoading(false);
              }
            );
          });
        })
        .catch(err => setLoading?.(false));
    } else {
      sendReportAsMail<IFakturaBildMail>(args).then(
        value => {
          setStatusText({ message: value, type: BannerTypeEnum.INFO });
          setLoading(false);
          setTimeout(() => {
            onClose();
          }, 2500);
        },
        value => {
          setStatusText({ message: value, type: BannerTypeEnum.ERROR });
          setLoading(false);
        }
      );
    }
  }, [args, images, onClose]);

  return (
    <Dialog
      fullscreen={isSmallScreen}
      open={props.open}
      classes={{
        contentWrapper: classes.dialogContentWrapper,
        paper: classes.dialogPaper,
        dialogHeader: classes.dialogHeader,
      }}
      onExited={() => {
        setReceivers('');
        setCopyReceivers('');
        setMailSubject('');
        setMailBody('');
        setLoading(false);
        setStatusText(null);
      }}
      onClose={() => {
        props.onClose();
      }}
      title="Skicka fakturabilder"
      footerActions={[
        {
          label: 'Avbryt',
          buttonProps: {
            disabled: loading || isSuccessful,
          },
          onClick: () => {
            props.onClose();
          },
        },
        {
          label: 'Skicka',
          buttonProps: {
            disabled: validateInputs().length != 0 || loading || isSuccessful,
            disabledReason: validateInputs(),
          },
          onClick: onSendClick,
        },
      ]}
    >
      <EDBox
        sx={{
          position: 'relative',
          top: theme => theme.spacing(-4),
          transform: `translateY(${statusText ? 32 : 0}px)`,
          opacity: statusText ? 1 : 0,
          transitionDuration: '0.5s',
          zIndex: 100,
        }}
      >
        <EDBox
          sx={theme => ({
            display: 'flex',
            flexFlow: 'row nowrap',
            alignItems: 'center',
            justifyContent:
              statusText && statusText?.type === BannerTypeEnum.ERROR
                ? 'space-between'
                : 'flex-start',
            position: 'absolute',
            width: 'calc(100% - ' + theme.spacing(2) + ')',
            backgroundColor: statusText
              ? statusText.type === BannerTypeEnum.INFO
                ? theme.palette.info.light
                : statusText.type === BannerTypeEnum.ERROR
                ? theme.palette.error.light
                : theme.palette.info.light
              : theme.palette.info.light,
            color: theme.palette.common.white,
            boxShadow: theme.shadows[3],
            zIndex: 100,
            padding: theme.spacing(1),
          })}
        >
          <EDTypography color={'white'}>{statusText?.message}</EDTypography>
          <EDBox component={'span'}>
            {statusText && statusText?.type === BannerTypeEnum.ERROR && (
              <EDIconButton
                icon="clear"
                tooltip="Stäng"
                margin="none"
                padding="dense"
                iconColor="action"
                onClick={() => {
                  setStatusText(null);
                }}
              />
            )}
          </EDBox>
        </EDBox>
      </EDBox>

      <EDBox
        sx={theme => ({
          padding: theme.spacing(2),
          width: isSmallScreen ? 'auto' : '550px',
        })}
      >
        {loading && <EDAvenyProgress />}

        <EDTextEdit
          autoFocus
          disabled={loading || isSuccessful}
          label={'Ange mottagarens mailadress'}
          onChange={newValue => setReceivers(newValue)}
          required
          value={receivers}
        />
        <EDTextEdit
          disabled={loading || isSuccessful}
          label={'Skicka kopia till'}
          onChange={newValue => setCopyReceivers(newValue)}
          value={copyReceivers}
        />
        <EDTextEdit
          disabled={loading || isSuccessful}
          label={'Ämne'}
          onChange={newValue => setMailSubject(newValue)}
          required
          value={mailSubject}
        />
        <EDTypography bold m={1} mb={0}>
          Bilagor:
          <EDTypography component="span">
            {' Alla aktuella fakturabilder bifogas i en rapport'}
          </EDTypography>
        </EDTypography>
        <EDTextEdit
          disabled={loading || isSuccessful}
          label={'Meddelande'}
          multiline
          onChange={newValue => setMailBody(newValue)}
          value={mailBody}
        />
        <EDTypography>
          {
            'Vid skickande av KOB- och e-fakturor skapas endast en bildsida. Större KOB-/e-fakturor, som inte får plats på en sida kan sparas ner i sin helhet via funktionen "Skriv ut" under trepunkters-knappen.'
          }
        </EDTypography>
      </EDBox>
    </Dialog>
  );
};

export default FakturaattestMailFakturaBildModal;
